import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';

interface TransactionData {
   _id: string;
   updatedAt: string;
   createdAt?: string;
   status: string;
   voucherCode: string;
   store: string;
   amount: number;
   platformFee: number;
   xenditFee: number;
   netAmount: number;
   vat: number;
   customer: string;
   smsStatus?: string;
   smsError?: string;
   seller?: string;
}

interface Props {
   data: TransactionData;
}

const TransactionDetail = ({ data }: Props) => {
   const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleString('en-PH', {
         year: 'numeric',
         month: 'short',
         day: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   return (
      <Wrapper>
         <StatusBadge $status={data.status}>
            {data.status === 'success' ? 'Successful' : 'Pending'}
         </StatusBadge>

         <Section>
            <SectionTitle>Transaction Info</SectionTitle>
            <Row>
               <Label>Transaction ID</Label>
               <Value $mono>{data._id}</Value>
            </Row>
            <Row>
               <Label>Date</Label>
               <Value>{formatDate(data.updatedAt)}</Value>
            </Row>
            <Row>
               <Label>Store</Label>
               <Value>{data.store}</Value>
            </Row>
            {data.seller && (
               <Row>
                  <Label>Seller</Label>
                  <Value>{data.seller}</Value>
               </Row>
            )}
            <Row>
               <Label>Customer</Label>
               <Value>{data.customer}</Value>
            </Row>
            <Row>
               <Label>Voucher Code</Label>
               <Value $mono>{data.voucherCode}</Value>
            </Row>
         </Section>

         <Divider />

         <Section>
            <SectionTitle>Payment Breakdown</SectionTitle>
            <Row>
               <Label>Amount</Label>
               <Value>{currencyFormat(data.amount, false)}</Value>
            </Row>
            <Row>
               <Label>
                  Platform Fee
                  {data.amount > 0 && data.platformFee > 0
                     ? ` (${Math.round(
                          (data.platformFee / data.amount) * 100
                       )}%)`
                     : ''}
               </Label>
               <Value $deduction>-{currencyFormat(data.platformFee, false)}</Value>
            </Row>
            <Row>
               <Label>Payment Gateway Fee</Label>
               <Value $deduction>-{currencyFormat(data.xenditFee, false)}</Value>
            </Row>
            <Row>
               <Label>VAT (12%)</Label>
               <Value $deduction>-{currencyFormat(data.vat, false)}</Value>
            </Row>
         </Section>

         <TotalRow>
            <TotalLabel>Net Amount</TotalLabel>
            <TotalValue>{currencyFormat(data.netAmount, false)}</TotalValue>
         </TotalRow>

         {data.smsStatus && data.smsStatus !== '' && (
            <>
               <Divider />
               <Section>
                  <SectionTitle>SMS Notification</SectionTitle>
                  <Row>
                     <Label>Status</Label>
                     <SmsBadge $status={data.smsStatus}>
                        {data.smsStatus === 'sent'
                           ? 'Sent'
                           : data.smsStatus === 'failed'
                           ? 'Failed'
                           : data.smsStatus === 'disabled'
                           ? 'Disabled'
                           : data.smsStatus}
                     </SmsBadge>
                  </Row>
                  {data.smsStatus === 'failed' && data.smsError && (
                     <Row>
                        <Label>Error</Label>
                        <SmsError>{data.smsError}</SmsError>
                     </Row>
                  )}
               </Section>
            </>
         )}
      </Wrapper>
   );
};

const Wrapper = styled.div`
   padding-top: 16px;
`;

const StatusBadge = styled.div<{ $status: string }>`
   display: inline-block;
   padding: 4px 12px;
   border-radius: 20px;
   font-size: 13px;
   font-weight: 600;
   margin-bottom: 16px;
   color: white;
   background: ${({ $status }) =>
      $status === 'success' ? colors.SUCCESS : colors.GRAY5};
`;

const Section = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const SectionTitle = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY4};
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-bottom: 2px;
`;

const Row = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

const Label = styled.span`
   font-size: 14px;
   color: ${colors.GRAY2};
`;

const Value = styled.span<{ $mono?: boolean; $deduction?: boolean }>`
   font-size: 14px;
   color: ${({ $deduction }) => ($deduction ? colors.DANGER : colors.GRAY1)};
   font-weight: 500;
   font-family: ${({ $mono }) => ($mono ? "'Courier New', monospace" : 'inherit')};
   ${({ $mono }) => $mono && 'font-size: 13px;'}
`;

const Divider = styled.div`
   height: 1px;
   background: ${colors.GRAY13};
   margin: 16px 0;
`;

const TotalRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-top: 16px;
   padding-top: 12px;
   border-top: 2px solid ${colors.GRAY12};
`;

const TotalLabel = styled.span`
   font-size: 15px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const TotalValue = styled.span`
   font-size: 18px;
   font-weight: 700;
   color: ${colors.SUCCESS};
`;

const SmsBadge = styled.span<{ $status: string }>`
   font-size: 12px;
   font-weight: 600;
   padding: 2px 10px;
   border-radius: 20px;
   color: ${({ $status }) =>
      $status === 'sent'
         ? colors.SUCCESS
         : $status === 'failed'
         ? colors.DANGER
         : colors.GRAY5};
   background: ${({ $status }) =>
      $status === 'sent'
         ? `${colors.SUCCESS}15`
         : $status === 'failed'
         ? `${colors.DANGER}15`
         : colors.GRAY14};
`;

const SmsError = styled.span`
   font-size: 12px;
   color: ${colors.DANGER};
   max-width: 250px;
   text-align: right;
   word-break: break-word;
`;

export default TransactionDetail;
